import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { fetchRunLog } from '../api.js'
import { distinctFailures } from '@qim/shared'
import type { RepeatedFailure } from '@qim/shared'

/**
 * A run's log, as it arrives.
 *
 * This is the feature that replaces watching a terminal over a screenshare, so
 * three things matter more than they look:
 *
 * **It appends rather than replaces.** Each poll passes the previous cursor and
 * receives only what is new, so the log survives the run finishing and a reader
 * scrolling back through it. Re-reading from the start every few seconds would
 * fight whoever is trying to read it.
 *
 * **It is not redacted here.** The lines were redacted in the customer's account
 * before they were written. A console-side filter would be reassurance rather
 * than a control — the data would already have left their account to reach it.
 *
 * **What it holds is text, and a bounded amount of it.** Both of those were
 * wrong until IM-83, and both were quadratic rather than merely large — see
 * below. This component is mounted once per *opened* disclosure now, which is
 * the other half of that fix and lives in `deployment.tsx`.
 */

/**
 * How many lines are kept in the browser.
 *
 * An apply runs for 30–90 minutes and the previous version kept every line it
 * was ever sent, so peak memory was a function of how long the run talked: at
 * 50,000 lines the buffer and its rendered string cost ~23MB of heap for 5.4MB
 * of actual text.
 *
 * The tail rather than the head, because a reader watching a live run wants the
 * end, and a reader diagnosing a failure wants the end even more. Nothing is
 * lost that was not already elsewhere: the full log is in the customer's own
 * CloudWatch group, and this endpoint pages.
 *
 * ~5,000 lines is about 2MB of text — comfortably more than anyone scrolls, and
 * bounded regardless of the run.
 */
const MAX_LINES = 5000

/**
 * ANSI escape sequences, which Terraform colourises its output with.
 *
 * CSI (`ESC[` … final byte in `@`-`~`) covers the SGR colour codes that make up
 * essentially all of it, plus the cursor moves the installer's progress lines
 * emit. The two-character form catches the stragglers a `[`-only pattern leaves.
 */
/*
 * The control character is the entire point: ESC is what starts an ANSI
 * sequence, so there is no way to match one without naming it. Hence the
 * suppression rather than a cleverer pattern.
 */
// eslint-disable-next-line no-control-regex
const ANSI = /\u001b\[[0-9;?]*[ -/]*[@-~]|\u001b[@-Z\\-_]/g

/**
 * `14:12:03  <message>` — the wall-clock time, which is what a reader scans.
 *
 * The escapes are stripped here, in the one place a line is touched exactly
 * once.
 *
 * **In the console, not in the customer's account.** The wrapper pipes the
 * installer through the redactor and writes to the customer's *own* log group —
 * that is their record of what was run, and quietly rewriting what Terraform
 * printed into it is not ours to do. The escapes are only a problem in this
 * rendering, so this is where they are dealt with.
 *
 * Losing the colour is the intended trade. What it costs is that errors are no
 * longer red; what it buys is that they are legible at all — `[31m` is red, so
 * before this the error lines carried the most escapes and were the hardest
 * rows on the screen to read, which is exactly backwards. Rendering the codes
 * as spans is the better answer and its own story: it means parsing untrusted
 * text from a customer's account into markup.
 */
const format = (line: { readonly at: string; readonly message: string }): string =>
  `${line.at.slice(11, 19)}  ${line.message.replace(ANSI, '')}`

/**
 * Keeps the last `MAX_LINES` of `text`, cutting on a line boundary.
 *
 * Counts newlines backwards from the end rather than splitting the whole string,
 * so trimming costs the size of the tail and not the size of the log — the point
 * of the cap is to bound the work as well as the memory.
 *
 * Finding the `MAX_LINES`-th newline from the end means there are more than
 * `MAX_LINES` lines, and the text after it is exactly `MAX_LINES` of them.
 * Running out of newlines first means the log still fits, and it is returned
 * untouched — which is what keeps a log of exactly the cap from losing its first
 * line and being reported as truncated.
 */
const trimToTail = (text: string): string => {
  let seen = 0
  let cut = text.length
  while (cut > 0) {
    const previous = text.lastIndexOf('\n', cut - 1)
    if (previous === -1) return text
    seen += 1
    if (seen === MAX_LINES) return text.slice(previous + 1)
    cut = previous
  }
  return text
}

export const RunLog = ({
  baseUrl,
  tenantId,
  runId,
  active,
  failed = false,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly runId: string
  /** While true, keep polling. A finished run is read once. */
  readonly active: boolean
  /**
   * Whether this run failed, which changes which end of the log to open at.
   *
   * A failed run's reason is always at the end and a plan emits thousands of
   * lines before it — one real failure was explained on a line past the first
   * page entirely, so the panel showed a plan that looked like it worked and
   * the reader had to page, or go to CloudWatch. A succeeded run is read from
   * the start, as before: there the beginning is where the story is.
   */
  readonly failed?: boolean
}): JSX.Element => {
  /**
   * The log as text, not as line objects.
   *
   * Formatting on arrival and appending to a string is the whole of the second
   * half of IM-83. Before, this held `{at, message}` objects and the render
   * re-joined all of them on every change — so both the append and the join
   * were O(total) per poll and O(total²) over a run. At 50,000 lines that
   * measured 2.5M element copies and 287MB of string allocation churned through
   * the GC, for a log of 5.4MB.
   *
   * A string append is not a copy in V8, so this is O(total) overall, and the
   * per-object overhead — about four times the text itself — is gone. The
   * structure was only ever kept to slice the timestamp, which `format` now
   * does once, when the line arrives.
   */
  const [text, setText] = useState('')
  const [truncated, setTruncated] = useState(false)
  const [note, setNote] = useState<string | undefined>(undefined)
  /**
   * The error Terraform reported, lifted out of the log it is buried in.
   *
   * Extracted from the page as it arrives rather than by keeping the lines
   * around, so the O(total) string append that IM-83 put here stays that way.
   */
  const [failures, setFailures] = useState<readonly RepeatedFailure[]>([])
  const cursor = useRef<string | undefined>(undefined)
  /*
   * The same text as `text`, readable synchronously.
   *
   * `poll` is the only writer, and it needs the current value to append to and
   * to decide whether the trim dropped anything. Doing that inside a `setText`
   * updater would mean calling `setTruncated` from it — a side effect in a
   * function React requires to be pure, and one StrictMode invokes twice.
   */
  const held = useRef('')

  useEffect(() => {
    let live = true

    const poll = async (): Promise<void> => {
      try {
        /*
          Tail only on the first read of a failed run. `after` and `tail` are
          two ways of choosing a page, so the adapter ignores the cursor when
          tailing; a failed run is terminal and therefore read exactly once,
          which is what makes that safe rather than a way to lose pages.
        */
        const page = await fetchRunLog(
          baseUrl,
          tenantId,
          runId,
          cursor.current,
          failed && cursor.current === undefined,
        )
        if (!live) return
        const found = distinctFailures(page.lines.map((line) => line.message))
        if (found.length > 0) setFailures(found)
        // CloudWatch returns the same forward token at the end of a stream, so
        // an empty page is the ordinary answer between one line and the next.
        if (page.lines.length > 0) {
          const chunk = page.lines.map(format).join('\n')
          const grown = held.current === '' ? chunk : `${held.current}\n${chunk}`
          const kept = trimToTail(grown)
          held.current = kept
          setText(kept)
          if (kept.length !== grown.length) setTruncated(true)
        }
        cursor.current = page.next ?? cursor.current
        setNote(page.note)
      } catch {
        // A failed read is not a failed run, and the next tick tries again.
      }
    }

    void poll()
    /*
     * The cleanup is registered whether or not this run is still going.
     *
     * Returning early for a finished run left `live` with nothing to clear it,
     * so that first poll could never be cancelled — and StrictMode mounts,
     * cleans up and mounts again, so *both* invocations appended and every
     * finished run's log rendered doubled. `active` is false for exactly the
     * logs people open most: the ones from a run that already ended.
     *
     * It is a development-only doubling, since React only double-invokes
     * effects in a dev build, but the missing cancellation is real either way —
     * a poll in flight when the disclosure closes would otherwise still append
     * to a component nobody is looking at.
     */
    const timer = active ? setInterval(() => void poll(), 4000) : undefined
    return () => {
      live = false
      if (timer !== undefined) clearInterval(timer)
    }
  }, [baseUrl, tenantId, runId, active, failed])

  if (text === '') {
    return <p role="status">{note ?? 'Waiting for the first line…'}</p>
  }

  const headline =
    failures.length === 0 ? null : (
      /*
        Above the log, not inside it. The recorded failure on a run says only
        that the installer exited non-zero, which is true and useless; this is
        the line that answers the question, and burying it in five thousand
        lines of plan output is what it is being lifted out of.

        **Every distinct failure, not the last one** (IM-141). An apply that
        died on two different things reported one of them, and the one it left
        out was the one that would fail the next run too — so somebody fixes
        what they were shown and loses another half hour. The installer retries
        four times, so the repetitions are collapsed and counted rather than
        listed.
      */
      <div className="failure-headline" role="alert">
        {failures.map((failure) => (
          <div key={failure.summary}>
            <strong>{failure.summary}</strong>
            {failure.resource === undefined && failure.attempts === 1 ? null : (
              <p className="muted">
                {failure.resource}
                {failure.resource !== undefined && failure.at !== undefined
                  ? ` — ${failure.at}`
                  : ''}
                {/* Said because it is the difference between "try again" and
                  "this needs fixing first" — four identical attempts is not a
                  transient error. */}
                {failure.attempts === 1
                  ? ''
                  : `${failure.resource === undefined ? '' : ' · '}failed on all ${String(failure.attempts)} attempts`}
              </p>
            )}
          </div>
        ))}
      </div>
    )

  return (
    <>
      {headline}
      {/* Said rather than silent: a reader who has scrolled to the top of a
        truncated log needs to know it is not the top of the run. */}
      {truncated ? (
        <p className="muted">
          Showing the last {MAX_LINES.toLocaleString('en')} lines. The full log is in the
          customer&rsquo;s own account.
        </p>
      ) : null}
      <pre className="run-log" aria-label={`Log for run ${runId}`}>
        {text}
      </pre>
    </>
  )
}
